import { useTour, type StepType } from '@reactour/tour';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  isSetupTourCompleted,
  markSetupTourCompleted,
  markSetupTourSkipped,
} from './onboardingService';
import { isTourTargetReady, watchTourTargetReady } from './targetReadiness';
import { requestTourRequiredFields } from './requiredFieldsPrompt';
import {
  performTourTargetAction,
  waitForTourTarget,
  waitForTourTargetReady,
  waitForTourTargetRemoval,
} from './targetRuntime';
import { selectorForTourTarget } from './tourTargets';
import type { SetupTourDefinition, SetupTourRegistry, SetupTourStep } from './types';
import { dispatchSiderExpandKeys } from '@/components/layout/Sider/siderExpand';
import { getStoredUser } from '@/services/auth';

export type SetupTourControllerHandle = {
  startTour: (tourId: string) => void;
  next: () => Promise<void>;
  goToStep: (stepId: string) => Promise<void>;
  previous: () => Promise<void>;
  close: () => void;
};

type SetupTourControllerProps = {
  registry: SetupTourRegistry;
  onActiveTourChange?: (tourId: string | null) => void;
  onTransitionChange?: (isTransitioning: boolean) => void;
};

const toReactourStep = (step: SetupTourStep): StepType => ({
  ...step,
  selector: selectorForTourTarget(step.highlightTarget ?? step.target),
});

const dispatchLifecycleActions = (actions: SetupTourStep['onBack']): void => {
  actions?.forEach((action) => {
    window.dispatchEvent(new Event(action.eventName));
  });
};

const shouldSkipForward = (step: SetupTourStep): boolean => {
  if (
    step.skipIfTargetPresent &&
    document.querySelector(selectorForTourTarget(step.skipIfTargetPresent))
  ) {
    return true;
  }

  if (step.skipUnlessTargetAttribute) {
    const { target, attribute, value } = step.skipUnlessTargetAttribute;
    return document.querySelector(selectorForTourTarget(target))?.getAttribute(attribute) !== value;
  }

  return false;
};

/**
 * Some steps target a one-time action (e.g. a button that unmounts itself once
 * clicked). If the tour returns to a point before such a step while its
 * precondition is already satisfied, forward navigation must skip it rather
 * than wait for a target that can never reappear. Branch steps likewise skip
 * unless the page publishes the state they describe.
 */
const resolveForwardIndex = (definition: SetupTourDefinition, fromIndex: number): number => {
  let index = fromIndex + 1;
  for (;;) {
    const candidate = definition.steps[index];
    if (!candidate || !shouldSkipForward(candidate)) return index;
    index += 1;
  }
};

export const SetupTourController = forwardRef<SetupTourControllerHandle, SetupTourControllerProps>(
  ({ registry, onActiveTourChange, onTransitionChange }, ref): null => {
    const { setCurrentStep, setIsOpen, setSteps } = useTour();
    const navigate = useNavigate();
    const location = useLocation();
    const currentPathRef = useRef(location.pathname);
    const activeDefinitionRef = useRef<SetupTourDefinition | null>(null);
    const activeTourIdRef = useRef<string | null>(null);
    const activeStepIndexRef = useRef(0);
    const isActiveRef = useRef(false);
    const isTransitioningRef = useRef(false);
    const transitionIdRef = useRef(0);
    const controllerDrivenClickRef = useRef(false);
    const advanceOnReadyCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
      currentPathRef.current = location.pathname;
    }, [location.pathname]);

    const setTransitioning = useCallback(
      (isTransitioning: boolean): void => {
        isTransitioningRef.current = isTransitioning;
        onTransitionChange?.(isTransitioning);
      },
      [onTransitionChange],
    );

    const stopAdvanceOnReadyWatch = useCallback((): void => {
      advanceOnReadyCleanupRef.current?.();
      advanceOnReadyCleanupRef.current = null;
    }, []);

    const clearActiveTour = useCallback((): void => {
      stopAdvanceOnReadyWatch();
      isActiveRef.current = false;
      activeDefinitionRef.current = null;
      activeTourIdRef.current = null;
      onActiveTourChange?.(null);
      setTransitioning(false);
      setIsOpen(false);
    }, [onActiveTourChange, setIsOpen, setTransitioning, stopAdvanceOnReadyWatch]);

    const completeTour = useCallback(
      async (definition: SetupTourDefinition, transitionId: number): Promise<void> => {
        try {
          await markSetupTourCompleted(definition.id, definition.onboardingKey);
        } catch {
          if (transitionId === transitionIdRef.current) clearActiveTour();
          return;
        }

        if (transitionId !== transitionIdRef.current) return;
        transitionIdRef.current += 1;
        clearActiveTour();
      },
      [clearActiveTour],
    );

    const goToStep = useCallback(
      async (stepIndex: number): Promise<void> => {
        stopAdvanceOnReadyWatch();
        const transitionId = transitionIdRef.current + 1;
        transitionIdRef.current = transitionId;
        setTransitioning(true);

        const definition = activeDefinitionRef.current;
        const step = definition?.steps[stepIndex];
        if (!definition || !step) {
          if (transitionId === transitionIdRef.current) clearActiveTour();
          return;
        }

        if (step.expandSidebarKeys && step.expandSidebarKeys.length > 0) {
          dispatchSiderExpandKeys(step.expandSidebarKeys);
        }

        if (step.navigateOnEnter !== false && currentPathRef.current !== step.route) {
          void navigate(step.route);
        }

        const hasTarget = await waitForTourTarget(step.target);
        if (transitionId !== transitionIdRef.current) return;
        if (!hasTarget) {
          setTransitioning(false);
          return;
        }

        if (step.highlightTarget) {
          const hasHighlightTarget = await waitForTourTarget(step.highlightTarget);
          if (transitionId !== transitionIdRef.current) return;
          if (!hasHighlightTarget) {
            setTransitioning(false);
            return;
          }
        }

        activeStepIndexRef.current = stepIndex;
        setCurrentStep(stepIndex);
        setIsOpen(true);
        if (step.showRequiredFieldsOnEnter) {
          requestTourRequiredFields(step.target);
        }

        if (step.advanceOnReady) {
          const readyTarget = step.advanceOnReady;
          // This watcher belongs to the active step rather than a short transition.
          // It remains live while the user fills the form, including across a failed
          // tour-driven Save, and is disconnected when goToStep/clearActiveTour runs.
          advanceOnReadyCleanupRef.current = watchTourTargetReady(readyTarget, () => {
            advanceOnReadyCleanupRef.current = null;
            if (
              !isActiveRef.current ||
              activeDefinitionRef.current !== definition ||
              activeStepIndexRef.current !== stepIndex
            ) {
              return;
            }
            if (step.next?.completesTour) {
              // The last step has nowhere to go on: the ready flip is its completion.
              const transitionId = transitionIdRef.current + 1;
              transitionIdRef.current = transitionId;
              setTransitioning(true);
              void completeTour(definition, transitionId);
              return;
            }
            void goToStep(resolveForwardIndex(definition, stepIndex));
          });
        }

        setTransitioning(false);
      },
      [
        clearActiveTour,
        completeTour,
        navigate,
        setCurrentStep,
        setIsOpen,
        setTransitioning,
        stopAdvanceOnReadyWatch,
      ],
    );

    const runNext = useCallback(
      async (skipAction = false): Promise<void> => {
        if (!isActiveRef.current || isTransitioningRef.current) return;

        const definition = activeDefinitionRef.current;
        const stepIndex = activeStepIndexRef.current;
        const step = definition?.steps[stepIndex];
        if (!definition || !step) return;

        if (!isTourTargetReady(step.next?.guardTarget)) return;

        const transitionId = transitionIdRef.current + 1;
        transitionIdRef.current = transitionId;
        setTransitioning(true);

        const actions =
          step.next?.actions ?? (step.next?.action && !skipAction ? [step.next.action] : undefined);

        if (actions) {
          for (const action of actions) {
            if (action.type === 'dispatch-event') {
              window.dispatchEvent(new Event(action.eventName));
              continue;
            }

            if (action.type === 'wait-until-ready') {
              const isReady = await waitForTourTargetReady(action.target);
              if (transitionId !== transitionIdRef.current) return;
              if (!isReady) {
                setTransitioning(false);
                return;
              }
              continue;
            }

            controllerDrivenClickRef.current = action.type === 'click';
            const didPerformAction = performTourTargetAction(step.target, action);
            controllerDrivenClickRef.current = false;
            if (!didPerformAction) {
              setTransitioning(false);
              return;
            }
          }
        }

        if (step.next?.staysOnStep && !skipAction) {
          setTransitioning(false);
          return;
        }

        if (step.next?.waitForTargetRemoval) {
          const didRemoveTarget = await waitForTourTargetRemoval(step.next.waitForTargetRemoval);
          if (transitionId !== transitionIdRef.current) return;
          if (!didRemoveTarget) {
            setTransitioning(false);
            return;
          }
        }

        if (transitionId !== transitionIdRef.current) return;

        if (step.next?.completesTour) {
          await completeTour(definition, transitionId);
          return;
        }

        await goToStep(resolveForwardIndex(definition, stepIndex));
      },
      [completeTour, goToStep, setTransitioning],
    );

    const next = useCallback((): Promise<void> => runNext(), [runNext]);

    const goToNamedStep = useCallback(
      async (stepId: string): Promise<void> => {
        if (!isActiveRef.current || isTransitioningRef.current) return;

        const definition = activeDefinitionRef.current;
        const stepIndex = definition?.steps.findIndex((step) => step.id === stepId) ?? -1;
        if (stepIndex < 0) return;

        await goToStep(stepIndex);
      },
      [goToStep],
    );

    const previous = useCallback(async (): Promise<void> => {
      if (!isActiveRef.current || isTransitioningRef.current) return;

      const definition = activeDefinitionRef.current;
      const step = definition?.steps[activeStepIndexRef.current];
      if (!definition || !step) return;

      setTransitioning(true);
      dispatchLifecycleActions(step.onBack);

      let previousIndex = Math.max(activeStepIndexRef.current - 1, 0);
      while (previousIndex > 0 && definition.steps[previousIndex]?.skipOnBack) {
        previousIndex -= 1;
      }

      await goToStep(previousIndex);
    }, [goToStep, setTransitioning]);

    const close = useCallback((): void => {
      const definition = activeDefinitionRef.current;
      const step = definition?.steps[activeStepIndexRef.current];
      dispatchLifecycleActions(step?.onClose);
      transitionIdRef.current += 1;

      if (definition && !isSetupTourCompleted(getStoredUser(), definition.onboardingKey)) {
        void markSetupTourSkipped(definition.id, definition.onboardingKey);
      }

      clearActiveTour();
    }, [clearActiveTour]);

    const startTour = useCallback(
      (tourId: string): void => {
        const definition = registry[tourId];
        if (!definition) return;

        stopAdvanceOnReadyWatch();
        transitionIdRef.current += 1;
        activeDefinitionRef.current = definition;
        activeTourIdRef.current = tourId;
        activeStepIndexRef.current = 0;
        isActiveRef.current = true;
        onActiveTourChange?.(tourId);
        setSteps?.(definition.steps.map(toReactourStep));
        void goToStep(0);
      },
      [goToStep, onActiveTourChange, registry, setSteps, stopAdvanceOnReadyWatch],
    );

    useEffect(() => stopAdvanceOnReadyWatch, [stopAdvanceOnReadyWatch]);

    useImperativeHandle(
      ref,
      () => ({ startTour, next, goToStep: goToNamedStep, previous, close }),
      [close, goToNamedStep, next, previous, startTour],
    );

    useEffect(() => {
      const handleTargetClick = (event: MouseEvent): void => {
        if (!isActiveRef.current || controllerDrivenClickRef.current) return;
        if (!(event.target instanceof Element)) return;

        const definition = activeDefinitionRef.current;
        const stepIndex = activeStepIndexRef.current;
        const step = definition?.steps[stepIndex];
        if (!definition || !step) return;

        if (
          step.directNextTargets?.some((target) =>
            event.target instanceof Element
              ? event.target.closest(selectorForTourTarget(target)) !== null
              : false,
          )
        ) {
          if (step.next?.actions) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
          void runNext(step.next?.actions ? false : true);
          return;
        }

        if (
          step.advanceOnTargetClick &&
          event.target.closest(selectorForTourTarget(step.target)) !== null
        ) {
          void goToStep(resolveForwardIndex(definition, stepIndex));
        }
      };

      window.addEventListener('click', handleTargetClick, true);
      return () => {
        window.removeEventListener('click', handleTargetClick, true);
      };
    }, [goToStep, runNext]);

    return null;
  },
);

SetupTourController.displayName = 'SetupTourController';
